// import { ICollectionsChatBundles } from 'src/types/Collections';
import useFilterStore from 'src/stores/filter';

import { ISecureChatInfo } from 'src/types/Collections';
import { IKeyword } from 'src/types/Keywords';
import { IProjectBundle, IProjectResponse } from 'src/types/Projects';

export interface IFilterSchemeItem {
    value: string | number;
    label: string;
}

type IFilterSchemeItems = IFilterSchemeItem[];
export interface IFilterOption {
    defaultValues: Obj;
    type: 'radio' | 'checkbox';
    id: number | string;
    subTitle?: string;
    selectAllTitle?: string;
    items: IFilterSchemeItems;
    maxItems: number | null;
    withClearButton: boolean;
    withSubmitButton: boolean;
}
export interface IFilterScheme {
    title: string;
    withExpanded?: boolean;
    options: IFilterOption[];
    defaultValues?: Obj;
    onSubmit: (data: any) => any;
}

export const FILTER_IDS = {
    MESSAGE_DATE: 'message_date',
    KEYWORDS: 'keywords',
    CHATS_AND_CHANNELS_PRIVATE: 'chats_and_channels_private',
    BUNDLES: 'bundles',
    PROJECTS: 'projects',
    STREAMS: 'streams',
    LEAD_FEEDBACK: 'lead_feedback',
} as const;

export const FILTER_IDS_MESSAGE_DATE = {
    ALL: 'all',
    WEEK: 'week',
    MONTH: 'month',
    CUSTOM: 'custom',
};

interface IGetFiltersParams {
    bundles: IProjectBundle[];
    chats: ISecureChatInfo[];
    keywords: IKeyword[];
    projects?: IProjectResponse[];
    streams?: Array<{ id: number; name: string | null; category: { name: string } }>;
}

export const getChatsFilter = (chats: ISecureChatInfo[], isMobile?: boolean): IFilterScheme => ({
    title: 'Чаты и каналы',
    withExpanded: !isMobile,
    options: [
        {
            defaultValues: { [FILTER_IDS.CHATS_AND_CHANNELS_PRIVATE]: useFilterStore.getState().privateChats },
            type: 'checkbox',
            id: FILTER_IDS.CHATS_AND_CHANNELS_PRIVATE,
            items: chats.map(({ id, name, telegram_link }) => ({
                value: String(id),
                label: String(name || telegram_link),
            })),
            selectAllTitle: 'Выбрать все',
            maxItems: isMobile ? 5 : 3,
            withSubmitButton: !!isMobile,
            withClearButton: !!isMobile,
        },
    ],
    onSubmit: (data: Obj) => {
        useFilterStore.getState().setPrivateChats(data[FILTER_IDS.CHATS_AND_CHANNELS_PRIVATE]);
    },
});

export const getDateFilter = (isMobile?: boolean): IFilterScheme => {
    const filterStore = useFilterStore.getState();
    // Сохраняем текущие значения дат из store - они не должны сбрасываться при изменении других фильтров
    const selectedDate = filterStore.date.length ? filterStore.date[0] : FILTER_IDS_MESSAGE_DATE.ALL;
    const dateFrom = filterStore.dateFrom || '';
    const dateTo = filterStore.dateTo || '';

    return {
        title: 'Дата лида',
        withExpanded: !isMobile,
        defaultValues: {
            [FILTER_IDS.MESSAGE_DATE]: [selectedDate],
            dateFrom: dateFrom,
            dateTo: dateTo,
        },
        options: [
            {
                defaultValues: {
                    [FILTER_IDS.MESSAGE_DATE]: [selectedDate],
                },
                type: 'radio',
                id: FILTER_IDS.MESSAGE_DATE,
                items: [
                    {
                        value: FILTER_IDS_MESSAGE_DATE.ALL,
                        label: 'Все',
                    },
                    {
                        value: FILTER_IDS_MESSAGE_DATE.WEEK,
                        label: 'Эта неделя',
                    },
                    {
                        value: FILTER_IDS_MESSAGE_DATE.MONTH,
                        label: 'Этот месяц',
                    },
                    {
                        value: FILTER_IDS_MESSAGE_DATE.CUSTOM,
                        label: 'Произвольный период',
                    },
                ],
                withSubmitButton: !!isMobile,
                withClearButton: false,
                maxItems: 4,
            },
        ],
        onSubmit: (data: Obj) => {
            const selectedPeriod = data[FILTER_IDS.MESSAGE_DATE]?.[0];
            if (!selectedPeriod) return;

            useFilterStore.getState().setDate([selectedPeriod]);

            if (selectedPeriod === FILTER_IDS_MESSAGE_DATE.CUSTOM) {
                // Сохраняем даты только если они заполнены
                const dateFrom = data.dateFrom;
                const dateTo = data.dateTo;

                // Применяем фильтр только если обе даты заполнены
                if (dateFrom && dateTo) {
                    useFilterStore.getState().setDateRange(dateFrom, dateTo);
                }
            } else {
                useFilterStore.getState().setDateRange(undefined, undefined);
            }
        },
    };
};

export const getKeywordsFilter = (keywords: IKeyword[], isMobile?: boolean): IFilterScheme => {
    // Сортируем ключевые слова по алфавиту
    const sortedKeywords = [...keywords].sort((a, b) => a.word.localeCompare(b.word, 'ru'));

    return {
        title: 'Ключевые слова',
        withExpanded: !isMobile,
        options: [
            {
                defaultValues: { [FILTER_IDS.KEYWORDS]: useFilterStore.getState().keywords },
                type: 'checkbox',
                id: FILTER_IDS.KEYWORDS,
                items: sortedKeywords.map(({ word }) => ({
                    value: word,
                    label: word,
                })),
                selectAllTitle: 'Выбрать все',
                maxItems: isMobile ? 5 : 3,
                withSubmitButton: !!isMobile,
                withClearButton: !!isMobile,
            },
        ],
        onSubmit: (data) => {
            useFilterStore.getState().setKeywords(data[FILTER_IDS.KEYWORDS]);
        },
    };
};

export const getBundlesFilter = (bundles: IProjectBundle[], isMobile?: boolean): IFilterScheme => ({
    title: 'Подборки',
    withExpanded: !isMobile,
    options: [
        {
            defaultValues: { [FILTER_IDS.BUNDLES]: useFilterStore.getState().bundles },
            type: 'checkbox',
            id: FILTER_IDS.BUNDLES,
            items: bundles.map(({ id, name }) => ({
                value: String(id),
                label: name || 'Подборка без имени',
            })),
            selectAllTitle: 'Выбрать все',
            maxItems: isMobile ? 5 : 3,
            withSubmitButton: !!isMobile,
            withClearButton: !!isMobile,
        },
    ],
    onSubmit: (data) => {
        useFilterStore.getState().setBundles(data[FILTER_IDS.BUNDLES]);
    },
});

export const getProjectsFilter = (projects: IProjectResponse[] = [], isMobile?: boolean): IFilterScheme => ({
    title: 'Проекты',
    withExpanded: !isMobile,
    options: [
        {
            defaultValues: { [FILTER_IDS.PROJECTS]: useFilterStore.getState().projects },
            type: 'checkbox',
            id: FILTER_IDS.PROJECTS,
            items: projects.map(({ id, name }) => ({
                value: String(id),
                label: name || 'Проект без имени',
            })),
            selectAllTitle: 'Выбрать все',
            maxItems: isMobile ? 5 : 3,
            withSubmitButton: !!isMobile,
            withClearButton: !!isMobile,
        },
    ],
    onSubmit: (data) => {
        useFilterStore.getState().setProjects(data[FILTER_IDS.PROJECTS]);
    },
});

export const getStreamsFilter = (
    streams: Array<{ id: number; name: string | null; category: { name: string } }> = [],
    isMobile?: boolean
): IFilterScheme => ({
    title: 'Потоки лидов',
    withExpanded: !isMobile,
    options: [
        {
            defaultValues: { [FILTER_IDS.STREAMS]: useFilterStore.getState().streams },
            type: 'checkbox',
            id: FILTER_IDS.STREAMS,
            items: streams.map(({ id, name, category }) => ({
                value: String(id),
                label: name || category.name || `Поток #${id}`,
            })),
            selectAllTitle: 'Выбрать все',
            maxItems: isMobile ? 5 : 3,
            withSubmitButton: !!isMobile,
            withClearButton: !!isMobile,
        },
    ],
    onSubmit: (data) => {
        useFilterStore.getState().setStreams(data[FILTER_IDS.STREAMS]);
    },
});

export const getLeadFeedbackFilter = (isMobile?: boolean): IFilterScheme => ({
    title: 'Оценка лида',
    withExpanded: !isMobile,
    options: [
        {
            defaultValues: { [FILTER_IDS.LEAD_FEEDBACK]: [useFilterStore.getState().leadFeedback] },
            type: 'radio',
            id: FILTER_IDS.LEAD_FEEDBACK,
            items: [
                {
                    value: 'all',
                    label: 'Все',
                },
                {
                    value: 'good',
                    label: 'Хороший лид',
                },
                {
                    value: 'bad',
                    label: 'Не подходит',
                },
            ],
            maxItems: 3,
            withSubmitButton: false,
            withClearButton: false,
        },
    ],
    onSubmit: (data) => {
        const value = data[FILTER_IDS.LEAD_FEEDBACK]?.[0];
        if (value === 'good' || value === 'bad' || value === 'all') {
            useFilterStore.getState().setLeadFeedback(value);
        }
    },
});

export const getFilters = ({
    chats,
    keywords,
    bundles,
    projects = [],
    streams = [],
}: IGetFiltersParams): IFilterScheme[] => [
    getDateFilter(),
    getProjectsFilter(projects),
    getBundlesFilter(bundles),
    getKeywordsFilter(keywords),
    getChatsFilter(chats),
    getStreamsFilter(streams),
];

export const getFeedLeadFilters = ({
    streams = [],
    isMobile,
}: {
    streams?: IGetFiltersParams['streams'];
    isMobile?: boolean;
}): IFilterScheme[] => [getLeadFeedbackFilter(isMobile), getDateFilter(isMobile), getStreamsFilter(streams, isMobile)];
